import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Modal } from './Modal';
import { api } from '../lib/api';
import { useSession } from '../lib/session';

/**
 * Accepting the terms and the privacy notice.
 *
 * ## Why this blocks rather than nags
 *
 * It covers the application until both are accepted. A banner would be the
 * polite choice and the wrong one: an acceptance somebody scrolled past is not
 * an acceptance, and the record would say they agreed when what they actually
 * did was dismiss something.
 *
 * ## Why the text is on screen rather than behind a link
 *
 * Somebody asked to agree to a document they have not seen has not agreed to
 * it. Both are here in full, scrollable, and the checkbox is under them —
 * which is also the only arrangement that makes the record defensible if it is
 * ever questioned.
 *
 * ## It renders through `Modal`, and that is not cosmetic
 *
 * The first version drew its own `fixed z-[60]` overlay inside the page, and it
 * shipped broken. `<main>` is `relative z-10` and therefore a stacking context,
 * so a z-60 child of it is still z-10 as far as the rest of the page is
 * concerned — and the footer painted straight over the Accept button. A gate
 * that blocks the whole application and cannot be accepted is about the worst
 * shape a bug can take.
 *
 * `Modal` portals to the root, which is what every other overlay in this
 * codebase already did.
 */
export function PolicyGate({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const { user, signOut } = useSession();
  const [ticked, setTicked] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const status = useQuery({
    queryKey: ['policies-outstanding'],
    queryFn: () => api.outstandingPolicies(),
    enabled: Boolean(user),
  });

  const accept = useMutation({
    mutationFn: async () => {
      for (const policy of status.data?.outstanding ?? []) {
        await api.acceptPolicy(policy.document, policy.version);
      }
    },
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['policies-outstanding'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  /*
   * Shown while unknown as well as while accepted.
   *
   * If this endpoint is unreachable the right behaviour is to let somebody
   * work rather than lock them out of a dispatch system over a checkbox.
   */
  if (!user || status.isPending || status.isError) return <>{children}</>;

  const outstanding = status.data?.outstanding ?? [];
  if (outstanding.length === 0) return <>{children}</>;

  const both = outstanding.length === 2;

  return (
    <>
      {children}

      <Modal
        wide
        title={both ? 'Before you start' : 'Something has changed'}
        subtitle={
          both
            ? 'Please read these two documents. They are short.'
            : 'This has been updated since you last accepted it, so please read it again.'
        }
        footer={
          <div className="space-y-3">
            {error && (
              <p role="alert" className="rounded-xl bg-danger-50 p-3 text-sm text-danger-700">
                {error}
              </p>
            )}

            {/*
              The whole label is the tap target, not the 20px box inside it.
              The mobile audit caught the first version at 16px against a 44px
              minimum — this is ticked with a thumb, outdoors.
            */}
            <label className="-m-2 flex cursor-pointer items-start gap-3 p-2 text-[15px] leading-relaxed">
              <input
                type="checkbox"
                className="mt-0.5 h-5 w-5 shrink-0"
                checked={ticked}
                onChange={(event) => setTicked(event.target.checked)}
              />
              <span>{status.data?.acceptanceLine}</span>
            </label>

            <div className="flex flex-wrap items-center justify-between gap-3">
              <button type="button" className="btn-quiet" onClick={() => void signOut()}>
                Sign out instead
              </button>

              <button
                type="button"
                className="btn-primary"
                disabled={!ticked || accept.isPending}
                onClick={() => accept.mutate()}
              >
                {accept.isPending && (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                )}
                Accept and continue
              </button>
            </div>

            <p className="text-[15px] leading-relaxed text-muted-foreground">
              Recorded against your name, the version above, and the time. Both are readable again
              at any time from the footer.
            </p>
          </div>
        }
      >
        <div className="space-y-6">
          {outstanding.map((policy) => (
            <article key={policy.document}>
              <h3 className="flex items-center gap-2 font-heading text-lg">
                <FileText className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                {policy.title}
              </h3>
              <p className="text-[15px] text-muted-foreground">Version {policy.version}</p>

              <div className="mt-3 space-y-3">
                {policy.sections.map((section) => (
                  <section key={section.heading}>
                    <h4 className="text-sm font-semibold">{section.heading}</h4>
                    <p className="mt-0.5 text-sm leading-relaxed text-muted-foreground">
                      {section.body}
                    </p>
                  </section>
                ))}
              </div>
            </article>
          ))}
        </div>
      </Modal>
    </>
  );
}
